// 189 §C → 190 §D — THE BOARD RETURNS, OVER BENTO.
//
// 189 built this as a view over the cases' `stages`: columns by stage type,
// and a drag APPENDED a stage element — which is why 189 (15) hid it: a board
// move must never write `Case.stages`. It comes back with two things replaced
// and nothing else: the COLUMNS come from a `bento_template` row (the list
// Bento's, chosen in the picker below), and the MOVE calls the hook's `move`
// with a case id and a column code — ONE BentoPlacement row through
// useBento, firm-wide (190 (3): a Bento is a view, never a record). The card
// is 189's card minus its stage-date line, which was the column's date and no
// longer exists, and minus 189's drag-dim (190 glance (i)(a), ruled: no
// drag-dim in a Bento). The column / drop / highlight surface moved to
// src/components/BentoBoard.tsx, shared with the two task Bentos.
//
// 190 GLANCE (iii)(b) — THE CASE CARD WEARS THE TASK CARD'S PANE. Laurent:
// "put the cases in individual panes (like tasks) so one could drag and drop
// instead of managing it with menus, which is cumbersome and defeats the
// visual Bento system". The cards DID drag (`draggable={canDrag}`); what
// defeated it was 189's MenuSelect rendered as the body of every card. Now
// the card is ONE LINE in EisenTaskCard's pane (its ground, border, accent
// edge, radius, padding, shadow, size): a ref slot as wide as the widest ref
// on the board (TypeTagBox's mirror — the table's rule, a column is as wide
// as its widest cell), the title as the central truncating field, the
// prospect badge after it where the task row keeps its status tag, and the
// keyboard path DISCREET: the same MenuSelect with no closed text and no
// placeholder row — a caret alone, `.board-move`, revealed by the card's
// hover or focus-within (App.css) and held while its menu is open. It stays
// in a role="presentation" stopper because a portalled menu's click bubbles
// through React to the card, which would open the case.
//
// THE GATE IS THE LIST'S for what is SHOWN: the cards are App's `cases`, the
// rows GET /cases serves under lateral.cases.view. Who may MOVE is the list
// Bento's own right (`surface.bento.list`, 190 §E) — the host computes
// `movable`, the server enforces it on the write.
//
// 190 GLANCE ROUND 2 [B] — THE PANE, WHOLE. The two list hosts used to build
// the strip (the picker, the Liste | Tableau control) and the board's inputs
// each for themselves; now EntityList hands this component the rows its
// filters left (`bento.pane(fi)`), and it renders the labelled picker, the
// empty state and the board from the hook's own state (`useBento`'s
// BentoState) — the column ladder (bentoColumnOf) applied here, once.
import React from "react";
import type { Dispatch } from "react";
import { useTranslation } from "../i18n";
import type { TFn } from "../lib/taxonomyTypes";
import { FS, RAD, CARD_PAD } from "../lib/tokens"; // 205 §3 — the card's own padding, one name
import MenuSelect from "../components/MenuSelect";
import TypeTagBox from "../components/TypeTagBox";
import BentoBoard from "../components/BentoBoard";
import type { BentoCard } from "../components/BentoBoard";
import type { BentoState } from "../hooks/useBento";
import { bentoColumnLabel, bentoColumnOf } from "../lib/crmDefinitions.mjs";
import { edgeTint } from "../utils"; // 190 round 7 [AF] — the one edge derivation
import { BADGE_RING_W } from "../lib/badgeStack"; // 201 §A.2 — the ring a value box must not clip

export type BoardCase = Readonly<{
  id: number;
  ref?: string | null;
  title?: string | null;
  status?: string | null;
  isProspect?: boolean | null;
  /** 190 round 6 [S] — which list the row is from; absent = the host's own kind. */
  kind?: "ca" | "cc";
}>;
/** the host's own kind and, when the template spans both, the other */
export type BoardKind = "ca" | "cc";
export type BoardOpen = Readonly<{ kind: BoardKind; id: number }>;

/** 190 glance round 2 [C] — one field the template shows on the card: the
 *  list's own column, resolved by the host (its header label, its cell
 *  renderer bound to the list's context), rendered on the card's second
 *  line. `render` is React's own FC over the row (an imported type). */
export type BoardField = Readonly<{ k: string; label: string; render: React.FC<BoardCase> }>;

type Props = Readonly<{
  /** The rows the list's filters left (EntityList's `fi`) — [B] (f) — and, when the template
   *  spans both kinds ([S]), the other list's rows behind them, each row tagged by `kind`. */
  cases: readonly BoardCase[];
  /** [C] — the template's chosen fields, in its order, already resolved to columns. */
  fields: readonly BoardField[];
  /** The list Bento's state for the HOST's kind: templates, the chosen one, columns, placements, the move. */
  bento: BentoState;
  /** [S] — the same hook over the OTHER kind (the same chosen template — the choice is per
   *  surface), so a merged board reads and writes each kind's own placements. */
  bentoOther?: BentoState | null;
  /** the host's own kind — the rows that carry no `kind` are its */
  kind: BoardKind;
  /** The viewer may move the cards (every row or none — `surface.bento.list` is one right). */
  movable: boolean;
  onOpen: Dispatch<BoardOpen>;
}>;

function stopEvent(e: React.SyntheticEvent): void {
  e.stopPropagation();
}

/** The ref, in the task card's chip size, mono — the slot's body and its mirror. */
const REF = { fontSize: FS.s10, color: "var(--ts)", whiteSpace: "nowrap" } as const;
/** 201 §A.2 — the headroom a value box keeps around its content for the viewer ring: `stackCut`'s own. */
const RING_PAD = BADGE_RING_W * 2;

export default function CaseBoardView({ cases, fields, bento, bentoOther = null, kind, movable, onOpen }: Props) {
  const { t, lang } = useTranslation() as { t: TFn; lang: string };
  const { columns } = bento;
  const live = cases.filter((c) => c.status !== "archived");
  /* 196 §A — the move menu names a column as the board's header does: in the app's language,
     `label` when that language has none. */
  const labelOf = (code: string): string => { const c = columns.find((k) => k.code === code); return c ? bentoColumnLabel(c, lang) : code; };
  /* the widest ref on this board, for every card's ref slot */
  let widestRef = "";
  for (const c of live) if (c.ref && c.ref.length > widestRef.length) widestRef = c.ref;
  const moveLabel = t("bento.move_to_column", { fallback: "Move to a Bento column" });
  /* 190 round 6 [S] — TWO KINDS ON ONE BOARD. A card's board id is `<kind>:<id>`
     (the two lists' ids collide — independent autoincrements); its column is
     read from ITS kind's placements (the host's hook, or `bentoOther`'s); a
     move is dispatched to the same hook. The tell is the kind, not the
     neighbours: a collective case's card carries the LIGHTER edge on the
     collective list's own Bento too. */
  const kindOf = (c: BoardCase): BoardKind => c.kind ?? kind;
  const stateOf = (k: BoardKind): BentoState => (k === kind ? bento : (bentoOther ?? bento));
  const boardId = (c: BoardCase): string => `${kindOf(c)}:${String(c.id)}`;
  const onMove = ({ itemId, column }: { itemId: number | string; column: string }): void => {
    const m = /^(ca|cc):(\d+)$/.exec(String(itemId));
    if (!m) return;
    stateOf(m[1] as BoardKind).move({ itemId: Number(m[2]), column });
  };
  const cards: BentoCard[] = live.map((c) => {
    const canDrag = movable;
    const k = kindOf(c);
    const st = stateOf(k);
    const col = bentoColumnOf(c.id, st.mine, st.firm, columns);
    const bid = boardId(c);
    return {
      id: bid,
      column: col,
      node: (
        <div
          className="board-card"
          draggable={canDrag}
          title={canDrag ? undefined : t("bento.no_move_right_bento", { fallback: "You cannot move cards on this Bento" })}
          onDragStart={(e) => { if (!canDrag) { e.preventDefault(); return; } e.dataTransfer.setData("text/plain", bid); e.dataTransfer.effectAllowed = "move"; }}
          role="button"
          tabIndex={0}
          onClick={() => { onOpen({ kind: k, id: c.id }); }}
          onKeyDown={(e) => { if (e.key === "Enter") onOpen({ kind: k, id: c.id }); }}
          /* EisenTaskCard's pane, line for line (its `q.accent` is the Bento's
             `var(--ac)`); the grab cursor stays the board's own tell. [C]: a
             template that names fields adds a SECOND LINE in small type under
             the ref-and-title line — the pane becomes a column of two rows.
             [S](ii) gave a COLLECTIVE case's edge a lighter tint of the SAME
             accent; round 7 [AF] RULED it away — "the collective cases look
             dimmed": a collective case is not a dimmed case, it takes the
             tree's GREEN (`--gn`, its own colour, no hex invented), and EVERY
             edge — a case's `--ac` too — is the light tint through the one
             derivation, `edgeTint` (utils). */
          style={{
            background: "var(--bc)",
            border: "1px solid var(--br)",
            borderLeft: `3px solid ${edgeTint(k === "cc" ? "var(--gn)" : "var(--ac)")}`,
            borderRadius: RAD.r6,
            padding: CARD_PAD,
            cursor: canDrag ? "grab" : "pointer",
            fontSize: FS.s11_5,
            boxShadow: "0 1px 3px rgba(0,0,0,.3)",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "hidden",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
            <TypeTagBox mirror={widestRef ? <span className="mono" style={REF}>{widestRef}</span> : null}>
              <span className="mono" style={REF}>{c.ref ?? ""}</span>
            </TypeTagBox>
            <span
              style={{ flex: "1 1 auto", minWidth: 0, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              title={c.title ?? undefined}
            >
              {c.title ?? ""}
            </span>
            {c.isProspect ? <span className="status status-prospect" style={{ flexShrink: 0 }}>{t("matters.prospect.badge", { fallback: "Prospect" })}</span> : null}
            {canDrag && columns.length > 1 ? (
              <span role="presentation" onClick={stopEvent} onKeyDown={stopEvent} style={{ flexShrink: 0, display: "inline-flex" }}>
                <MenuSelect
                  className="board-move"
                  ariaLabel={moveLabel}
                  title={moveLabel}
                  value=""
                  onChange={(v) => { if (v) onMove({ itemId: bid, column: v }); }}
                  options={columns.filter((k) => k.code !== col).map((k) => [k.code, labelOf(k.code)] as const)}
                />
              </span>
            ) : null}
          </div>
          {fields.length > 0 ? (
            /* [C] — the template's fields under the title, ONE ROW PER FIELD
               (label · value, the value through the list's own cell renderer,
               single-line and ellipsising): every card of a template renders
               the same rows, so [E]'s invariant — every card in a Bento is one
               height — holds by construction, and the template's author sets
               the density (two fields, three lines). MEASURED at 312px panes:
               four fields on one wrapping line stood 97px beside 78px
               neighbours, and on one clipping line left no value readable. */
            <div style={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", columnGap: 6, rowGap: 1, alignItems: "center", fontSize: FS.s10, color: "var(--ts)", minWidth: 0 }}>
              {fields.map((f) => (
                <React.Fragment key={f.k}>
                  <span style={{ color: "var(--tm)", whiteSpace: "nowrap" }}>{f.label}</span>
                  {/* 201 §A.2 — THE CLIP BOX GROWS PAST THE RING, THE CONTENT BOX
                     DOES NOT MOVE. `overflow: hidden` clips at the PADDING box, and
                     this box was exactly its content's height, so a badge stack's
                     viewer ring — a box-shadow 1.5px OUTSIDE the badge (badgeStack.ts
                     BADGE_SELF_RING) — was cut flat at the top and bottom, and on the
                     left when he sat first (measured at 4× on the list Bento's
                     cards, both stacks, before any change to them). Padding on all
                     four sides with the same negative margin leaves the content
                     box — the ellipsis edge, the row's height, the 6px column gap —
                     byte for byte where it was, and widens only what the clip
                     admits. The headroom is `stackCut`'s own, 2 × BADGE_RING_W: 118
                     §1 measured a pad equal to the ring cutting its last pixel row
                     flat, and twice the ring whole. */}
                  <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: RING_PAD, margin: -RING_PAD }}>{f.render(c)}</span>
                </React.Fragment>
              ))}
            </div>
          ) : null}
        </div>
      ),
    };
  });
  return (
    <>
      {/* 190 [I] — no picker row here: the host renders BentoPicker in the
          icon's row (EntityList's `picker` capability). */}
      {bento.templateCode === null ? (
        <div style={{ fontSize: FS.s11, color: "var(--tm)", fontStyle: "italic", padding: "12px 2px" }}>
          {t("bento.no_template_path", { fallback: "No Bento template offers this surface yet — Settings › Custom lists › Taxonomies › Bento Templates." })}
        </div>
      ) : (
        <BentoBoard
          columns={columns}
          cards={cards}
          movable={new Set(movable ? cards.map((k) => k.id) : [])}
          onMove={onMove}
          emptyLabel={t("bento.empty_column_case", { fallback: "No case here" })}
        />
      )}
    </>
  );
}
